/**
 * Architecture §20's reuse rules, as predicates.
 *
 * "Reuse within the session when camera unchanged, viewport similar, player unchanged, required
 * capabilities covered, tracking quality acceptable. Recalibrate when camera changes, player
 * changes, a new capability is needed, confidence falls, a parent requests it."
 *
 * Deliberately pure functions rather than wiring. The launch lifecycle that consults them is
 * Slice D's, and the parent's `RESTART_CALIBRATION` override is Slice G's (D-041(1)); a predicate
 * that nobody calls yet is honest in a way a half-connected override would not be, and both slices
 * get to call the same rule rather than re-deriving it.
 *
 * Every refusal is reported, not just the first one met: "your sister is playing on a different
 * camera" is two facts, and a screen that could only ever say one of them would be lying by
 * omission.
 */

import { CALIBRATION_TUNABLES } from '../../platform/config/calibration';
import type { InputCapability } from '../../shared/types/capabilities';
import type { Viewport } from '../../shared/types/presentation';
import type { CameraMode } from '../camera/readiness';
import type { CalibrationProfile } from './calibration-profile';

export type CalibrationReuseRefusal =
  | 'NO_PROFILE'
  | 'CAMERA_CHANGED'
  | 'PLAYER_CHANGED'
  | 'VIEWPORT_CHANGED'
  | 'CAPABILITY_MISSING'
  | 'MODE_CHANGED'
  | 'QUALITY_TOO_LOW';

export interface CalibrationRequirement {
  readonly playerId: string;
  readonly cameraDeviceId?: string | undefined;
  readonly viewport: Viewport;
  /** What the mission about to run actually asks a body to do. */
  readonly requiredCapabilities: readonly InputCapability[];
  readonly cameraMode: CameraMode;
  /** Overrides the configured floor; a mission may be stricter than the default. */
  readonly minQuality?: number | undefined;
  /** GDS §32's parent override (wired in Slice G). */
  readonly parentRequested?: boolean | undefined;
}

export interface CalibrationReuseDecision {
  readonly reuse: boolean;
  readonly refusals: readonly CalibrationReuseRefusal[];
}

/** Similar, not identical: a browser chrome appearing is not a new room. */
function viewportIsSimilar(a: Viewport, b: Viewport): boolean {
  const tolerance = CALIBRATION_TUNABLES.reuse.viewportTolerance;
  const similar = (left: number, right: number): boolean => {
    if (left <= 0 || right <= 0) return false;
    return Math.abs(left - right) / Math.max(left, right) <= tolerance;
  };
  return similar(a.width, b.width) && similar(a.height, b.height);
}

export function evaluateCalibrationReuse(
  profile: CalibrationProfile | null,
  requirement: CalibrationRequirement,
): CalibrationReuseDecision {
  if (profile === null) return { reuse: false, refusals: ['NO_PROFILE'] };

  const refusals: CalibrationReuseRefusal[] = [];

  if (profile.playerId !== requirement.playerId) refusals.push('PLAYER_CHANGED');

  // An unknown device id on either side is not evidence of a change: browsers withhold device ids
  // until permission is granted, and refusing to reuse on that basis would recalibrate a child who
  // never moved.
  if (
    profile.cameraDeviceId !== undefined &&
    requirement.cameraDeviceId !== undefined &&
    profile.cameraDeviceId !== requirement.cameraDeviceId
  ) {
    refusals.push('CAMERA_CHANGED');
  }

  if (!viewportIsSimilar(profile.viewport, requirement.viewport)) refusals.push('VIEWPORT_CHANGED');

  if (profile.cameraMode !== requirement.cameraMode) refusals.push('MODE_CHANGED');

  const covered = new Set(profile.supportedCapabilities);
  if (requirement.requiredCapabilities.some((capability) => !covered.has(capability))) {
    refusals.push('CAPABILITY_MISSING');
  }

  const floor = requirement.minQuality ?? CALIBRATION_TUNABLES.reuse.minQuality;
  if (profile.quality < floor) refusals.push('QUALITY_TOO_LOW');

  return { reuse: refusals.length === 0, refusals };
}

/**
 * Architecture §20's recalibration triggers.
 *
 * The first four are the reuse rules read the other way round; the fifth — a parent asking — is
 * unconditional, because a grown-up who can see the child knows something the numbers do not.
 */
export function needsRecalibration(
  profile: CalibrationProfile | null,
  requirement: CalibrationRequirement,
): boolean {
  if (requirement.parentRequested === true) return true;
  return !evaluateCalibrationReuse(profile, requirement).reuse;
}

/**
 * "Confidence falls" (architecture §20), as a predicate over a live quality reading.
 *
 * A missing reading is not a fall: nothing observed and observed-to-be-poor are different states,
 * and interrupting a child because a frame had not arrived yet is the kind of recalibration prompt
 * that ends a session.
 */
export function hasTrackingQualityFallen(
  profile: CalibrationProfile,
  observedQuality: number | null,
): boolean {
  if (observedQuality === null || !Number.isFinite(observedQuality)) return false;
  return observedQuality < profile.quality * CALIBRATION_TUNABLES.reuse.qualityFallFraction;
}
